/**
 * Work / Register calves. Batches go: preparing, file made, sent, results back. Each calf shows what
 * it's missing with a button to fix it in its own record; rejections become tasks on the calf.
 */
import { useMemo, useState, type ReactNode } from "react";
import { Link, Route, Routes, useLocation, useNavigate, useSearchParams } from "react-router";
import { localDate, type JobDetail, type JobItemDTO, type JobSummary } from "../../../contract";
import { openRecord } from "../../../web/forms/EventForm";
import { ApiError, api } from "../../../web/lib/api";
import { formatDate } from "../../../web/lib/format";
import { plural, t, useLanguage } from "../../../web/lib/i18n";
import { invalidateAll, useApi } from "../../../web/lib/query";
import { useCan, useMe } from "../../../web/lib/session";
import { Page } from "../../../web/shell/Layout";
import { Button, Card, Checkbox, Chip, Disclosure, Empty, ErrorNote, Field, Icon, List, Loading, PageHeader, Progress, Row, Section, Segmented, Select, Sheet, Tabs, TextInput, Toggle, cx, toast } from "../../../web/ui/kit";
import { associationShort } from "../shared/epd";
import { WORKSPACE_SECTION_LABELS } from "../shared/labels";
import type { ReadinessCheck, RegistrationBatchDTO, RegistrationRow } from "../shared/types";
import { CheckChips, StageSteps, fileUrl, openPrefilled, readFileText } from "./ui";

type Batch = JobSummary & { data: Record<string, unknown>; created_at: string };

function useToday() {
  const { data: me } = useMe();
  return me?.ranch ? localDate(new Date(), me.ranch.timezone) : new Date().toISOString().slice(0, 10);
}

/** Open the right record to fix a readiness problem. */
export function fixCheck(c: ReadinessCheck, row: Pick<RegistrationRow, "animal" | "item_id">, go: (to: string) => void, onItem?: () => void, association?: string) {
  if (!c.fix) return;
  if (c.fix.kind === "item") return onItem?.();
  const id = c.fix.animal_id ?? row.animal.id;
  if (c.fix.kind === "animal") { go(`/cattle/${id}`); return; }
  if (c.fix.type === "genetics.registration") return openRecord({ initial: { type: c.fix.type, subjects: [{ kind: "animal", id, role: "animal" }], data: { status: "registered", ...(association ? { association } : {}) } } });
  if (c.fix.type === "genetics.pedigree_correction") return openRecord({ initial: { type: c.fix.type, subjects: [{ kind: "animal", id, role: "animal" }], data: { role: c.id === "dam_registered" ? "dam" : "sire" } } });
  if (c.fix.type) openPrefilled(c.fix.type, [{ kind: "animal", id, role: "animal" }], { context: "birth", kind: "birth" });
}

function OutcomeChip({ row }: { row: RegistrationRow }) {
  const o = row.outcome;
  if (row.item_status === "skipped") return <Chip tone="neutral">{t("Left out")}</Chip>;
  if (!o) return row.exported ? <Chip tone="neutral" icon="file">{t("In the file")}</Chip> : row.ready ? <Chip tone="good" icon="check">{t("Ready")}</Chip> : null;
  if (o.status === "registered") return <Chip tone="good" icon="award">{o.number ? t("Registered {{number}}", { number: o.number }) : t("Registered")}</Chip>;
  if (o.status === "rejected") return <Chip tone="warn" icon="warn">{t("Rejected")}</Chip>;
  if (o.status === "pending") return <Chip tone="warn" icon="clock">{t("On hold")}</Chip>;
  if (o.status === "submitted") return <Chip tone="info" icon="send">{t("Sent")}</Chip>;
  return <Chip tone="neutral">{t("Recorded, not registered")}</Chip>;
}

// ---------------------------------------------------------------- workspace pages

function Shell({ children, actions }: { children: ReactNode; actions?: ReactNode }) {
  const loc = useLocation();
  const current = loc.pathname.endsWith("/transfers") ? "transfers" : "batches";
  return (
    <Page>
      <PageHeader back={{ to: "/work", label: t("Work") }} title={t("Register calves")} subtitle={t("Batches, readiness, files, returned numbers and rejections.")} actions={actions} />
      <Tabs value={current} tabs={[{ id: "batches", label: t(WORKSPACE_SECTION_LABELS.batches), to: "/work/registration" }, { id: "transfers", label: t(WORKSPACE_SECTION_LABELS.transfers), to: "/work/registration/transfers" }]} />
      {children}
    </Page>
  );
}

function Batches() {
  useLanguage();
  const can = useCan();
  const q = useApi<{ batches: Batch[]; seasons: Array<{ season: string; count: number }> }>("/m/genetics/registration");
  const open = (q.data?.batches ?? []).filter((b) => b.status !== "done");
  const done = (q.data?.batches ?? []).filter((b) => b.status === "done");
  return (
    <Shell actions={can("record") && <Button variant="primary" icon="plus" to="/work/registration/new">{t("Start a batch")}</Button>}>
      {q.isLoading ? <Loading /> : !q.data ? <ErrorNote error={q.error} retry={() => q.refetch()} /> : (
        <>
          {q.data.seasons.length > 0 && can("record") && (
            <Card>
              <div className="genetics-row-head">
                <Icon name="award" />
                <span className="genetics-row-title">{q.data.seasons.map((s) => (s.count === 1 ? t("{{count}} calf born in {{season}} isn't registered or in a batch", { count: 1, season: s.season }) : t("{{count}} calves born in {{season}} aren't registered or in a batch", { count: s.count, season: s.season }))).join(" · ")}</span>
                <Button size="sm" variant="primary" to={`/work/registration/new?season=${q.data.seasons[0].season}`}>{t("Start a batch")}</Button>
              </div>
            </Card>
          )}
          <Section title={t("In progress")} count={open.length}>
            {!open.length ? <Empty icon="award" title={t("No batches in progress")} body={t("Start one when calves are ready. Say “register the spring calves” or tap Start a batch.")} /> : (
              <div className="cards">
                {open.map((b) => (
                  <Card key={b.id} to={`/work/jobs/${b.id}`} className="job-card">
                    <span className="job-kind"><Icon name="award" size={14} />{associationShort(String(b.data.association ?? ""))}</span>
                    <span className="job-title">{b.title}</span>
                    <StageSteps stages={["prepared", "exported", "submitted", b.stage === "rejected" ? "rejected" : b.stage === "partial" ? "partial" : "accepted"]} current={b.stage} status={b.status} />
                    {b.progress.total > 0 && <Progress done={b.progress.done} total={b.progress.total} exceptions={b.progress.exceptions} />}
                  </Card>
                ))}
              </div>
            )}
          </Section>
          {done.length > 0 && (
            <Section title={t("Finished")} count={done.length}>
              <List>{done.map((b) => <Row key={b.id} icon="award" tone="genetics" to={`/work/jobs/${b.id}`} title={b.title} meta={plural(b.progress.done, "{{count}} registered", "{{count}} registered")} />)}</List>
            </Section>
          )}
        </>
      )}
    </Shell>
  );
}

function NewBatch() {
  useLanguage();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { data: me } = useMe();
  const today = useToday();
  const settings = (me?.ranch?.settings.genetics as { association?: string; membership?: string } | undefined) ?? {};
  const [association, setAssociation] = useState(settings.association ?? "");
  const [season, setSeason] = useState(params.get("season") ?? today.slice(0, 4));
  const [membership, setMembership] = useState(settings.membership ?? "");
  const [dna, setDna] = useState(false);
  const focus = params.get("animal");
  const [skip, setSkip] = useState<Set<string> | null>(null);
  const [busy, setBusy] = useState(false);
  const q = useApi<{ rows: RegistrationRow[] }>(association.trim() ? `/m/genetics/registration/candidates?season=${season}&association=${encodeURIComponent(association.trim())}${dna ? "&dna=1" : ""}` : null);
  const rows = q.data?.rows ?? [];
  const go = useNavigate();
  // From a calf's page only that calf starts picked; otherwise every candidate does.
  const skipped = skip ?? new Set(focus ? rows.filter((r) => r.animal.id !== focus).map((r) => r.animal.id) : []);
  const chosen = rows.filter((r) => !skipped.has(r.animal.id));
  const start = async () => {
    setBusy(true);
    try {
      const { id } = await api.post<{ id: string }>("/m/genetics/registration", { association: association.trim(), season, membership: membership || undefined, require_dna: dna, animal_ids: chosen.map((r) => r.animal.id) });
      await invalidateAll();
      navigate(`/work/jobs/${id}`);
    } catch (err) { toast(err instanceof ApiError ? err.message : t("Something went wrong on our side. Try again."), { tone: "bad" }); } finally { setBusy(false); }
  };
  const year = Number(today.slice(0, 4));
  return (
    <Page>
      <PageHeader back={{ to: "/work/registration", label: t("Register calves") }} title={t("Start a batch")} />
      <Field label={t("Association")}><TextInput value={association} onChange={(e) => setAssociation(e.target.value)} placeholder={"Red Angus Association of America"} /></Field>
      <div className="filter-row">
        <Select aria-label={t("Season")} value={season} onChange={(e) => setSeason(e.target.value)}>{[year, year - 1, year - 2].map((y) => <option key={y} value={y}>{t("Born in {{year}}", { year: y })}</option>)}</Select>
      </div>
      <Field label={t("Member number")} optional><TextInput value={membership} onChange={(e) => setMembership(e.target.value)} /></Field>
      <Toggle checked={dna} onChange={setDna} label={t("DNA parent check required")} hint={t("Turn on when this association needs DNA-verified parents.")} />
      {!association.trim() ? <p className="muted">{t("Type the association to see which calves are ready.")}</p> : q.isLoading ? <Loading /> : (
        <Section title={t("Calves")} count={rows.length} hint={t("Steers are left out. Tap a missing item to fix it in that calf's record.")}>
          {!rows.length ? <Empty icon="calf" title={t("No unregistered calves born in {{year}}", { year: season })} /> : (
            <ul className="genetics-rows">
              {rows.map((r) => (
                <li key={r.animal.id} className={cx("genetics-row", skipped.has(r.animal.id) && "is-skipped")}>
                  <div className="genetics-row-head">
                    <Checkbox checked={!skipped.has(r.animal.id)} onChange={(v) => { const n = new Set(skipped); if (v) n.delete(r.animal.id); else n.add(r.animal.id); setSkip(n); }} label={t("Include {{calf}}", { calf: r.animal.label })} />
                    <span className="genetics-row-title"><Link to={`/cattle/${r.animal.id}`} className="mono">{r.animal.label}</Link><span className="muted">{[r.sire?.label, r.dam?.label].filter(Boolean).join(" × ")}</span></span>
                  </div>
                  <CheckChips checks={r.checks} onFix={(c) => fixCheck(c, r, go, undefined, association.trim() || undefined)} />
                </li>
              ))}
            </ul>
          )}
        </Section>
      )}
      <div className="form-actions"><Button variant="primary" size="lg" icon="check" busy={busy} disabled={!association.trim() || !chosen.length} onClick={() => void start()}>{plural(chosen.length, "Start with {{count}} calf", "Start with {{count}} calves")}</Button></div>
    </Page>
  );
}

function Transfers() {
  useLanguage();
  const can = useCan();
  const q = useApi<{ rows: Array<{ animal: { id: string; label: string }; registration: { value: string; issuer: string | null }; transfer: { status: string; on: string | null } | null; departed: string | null }> }>("/m/genetics/registration-transfers");
  return (
    <Shell>
      <p className="muted">{t("Registered cattle that left the ranch. Their papers go to the buyer through the association.")}</p>
      {q.isLoading ? <Loading /> : !q.data?.rows.length ? <Empty icon="handshake" title={t("No transfers waiting")} /> : (
        <List>
          {q.data.rows.map((r) => (
            <Row key={r.animal.id} icon="handshake" tone="genetics" title={<Link to={`/cattle/${r.animal.id}`}>{r.animal.label}</Link>}
              meta={[`${r.registration.issuer ? associationShort(r.registration.issuer) : ""} ${r.registration.value}`.trim(), r.departed ? t("left {{date}}", { date: formatDate(r.departed) }) : null, r.transfer ? (r.transfer.status === "submitted" ? t("sent to association") : t("paperwork ready")) : t("not started")].filter(Boolean).join(" · ")}
              trailing={can("record") ? <Button size="sm" onClick={() => openRecord({ initial: { type: "genetics.registration_transfer", subjects: [{ kind: "animal", id: r.animal.id, role: "animal", label: r.animal.label }], data: { association: r.registration.issuer ?? "", status: r.transfer?.status === "prepared" ? "submitted" : r.transfer ? "completed" : "prepared" } } })}>{t("Record transfer")}</Button> : undefined} />
          ))}
        </List>
      )}
    </Shell>
  );
}

export function RegistrationWorkspace() {
  return (
    <Routes>
      <Route index element={<Batches />} />
      <Route path="new" element={<NewBatch />} />
      <Route path="transfers" element={<Transfers />} />
      <Route path="*" element={<Batches />} />
    </Routes>
  );
}

// ---------------------------------------------------------------- the batch (job page)

interface ReturnRow { position: number; animal: { id: string; label: string } | null; ambiguous: boolean; status: "registered" | "rejected" | "pending"; number?: string; reason?: string; tag: string | null }

function ResultsSheet({ open, onClose, rows, jobId, onDone }: { open: boolean; onClose: () => void; rows: RegistrationRow[]; jobId: string; onDone: () => void }) {
  const today = useToday();
  const waiting = rows.filter((r) => r.item_status !== "skipped" && r.exported && r.outcome?.status !== "registered");
  const [on, setOn] = useState(today);
  const [entries, setEntries] = useState<Record<string, { status: "registered" | "rejected" | "pending"; number?: string; reason?: string }>>({});
  const [busy, setBusy] = useState(false);
  const save = async () => {
    const results = Object.entries(entries).filter(([, e]) => e.status !== "registered" || e.number?.trim()).map(([animal_id, e]) => ({ animal_id, ...e }));
    if (!results.length) return;
    setBusy(true);
    try {
      const r = await api.post<{ filed: number }>(`/m/genetics/registration/${jobId}/results`, { on, results });
      await invalidateAll();
      toast(plural(r.filed, "Filed {{count}} result", "Filed {{count}} results"), { tone: "good" });
      setEntries({});
      onDone();
      onClose();
    } catch (err) { toast(err instanceof ApiError ? err.message : t("Something went wrong on our side. Try again."), { tone: "bad" }); } finally { setBusy(false); }
  };
  return (
    <Sheet open={open} onClose={onClose} title={t("Enter results")} size="lg">
      <form onSubmit={(e) => { e.preventDefault(); void save(); }} className="stack">
        <Field label={t("Results came back on")}><TextInput type="date" value={on} onChange={(e) => setOn(e.target.value)} /></Field>
        <ul className="genetics-rows">
          {waiting.map((r) => {
            const e = entries[r.animal.id];
            return (
              <li key={r.animal.id} className="genetics-row">
                <div className="genetics-row-head"><span className="genetics-row-title mono">{r.animal.label}</span>
                  <Segmented label={t("Result for {{calf}}", { calf: r.animal.label })} value={e?.status ?? ""} onChange={(v) => setEntries({ ...entries, [r.animal.id]: { ...e, status: v as "registered" } })}
                    options={[{ value: "registered", label: t("Registered") }, { value: "rejected", label: t("Rejected") }, { value: "pending", label: t("On hold") }]} />
                </div>
                {e?.status === "registered" && <TextInput aria-label={t("Registration number")} className="mono" placeholder={t("Registration number")} value={e.number ?? ""} onChange={(x) => setEntries({ ...entries, [r.animal.id]: { ...e, number: x.target.value } })} />}
                {(e?.status === "rejected" || e?.status === "pending") && <TextInput aria-label={t("Reason")} placeholder={t("Reason")} value={e.reason ?? ""} onChange={(x) => setEntries({ ...entries, [r.animal.id]: { ...e, reason: x.target.value } })} />}
              </li>
            );
          })}
        </ul>
        <div className="form-actions"><Button variant="primary" size="lg" type="submit" busy={busy} disabled={!Object.keys(entries).length}>{t("File results")}</Button></div>
      </form>
    </Sheet>
  );
}

function ReturnedFile({ jobId, onDone }: { jobId: string; onDone: () => void }) {
  const today = useToday();
  const [preview, setPreview] = useState<{ file_id: string; duplicate_file: boolean; rows: ReturnRow[] } | null>(null);
  const [on, setOn] = useState(today);
  const [busy, setBusy] = useState(false);
  const read = async (file: File) => {
    setBusy(true);
    try { setPreview(await api.post(`/m/genetics/registration/${jobId}/results/preview`, { text: await readFileText(file), name: file.name })); }
    catch (err) { toast(err instanceof ApiError ? err.message : t("Something went wrong on our side. Try again."), { tone: "bad" }); } finally { setBusy(false); }
  };
  const matched = preview?.rows.filter((r) => r.animal) ?? [];
  const apply = async () => {
    if (!preview) return;
    setBusy(true);
    try {
      const r = await api.post<{ filed: number; unchanged: number }>(`/m/genetics/registration/${jobId}/results`, { on, file_id: preview.file_id, results: matched.map((m) => ({ animal_id: m.animal!.id, status: m.status, number: m.number, reason: m.reason })) });
      await invalidateAll();
      toast(r.filed ? plural(r.filed, "Filed {{count}} result", "Filed {{count}} results") : t("Nothing new in this file"), { tone: "good" });
      setPreview(null);
      onDone();
    } catch (err) { toast(err instanceof ApiError ? err.message : t("Something went wrong on our side. Try again."), { tone: "bad" }); } finally { setBusy(false); }
  };
  if (!preview) return <Field label={t("The association's file")} hint={t("A CSV with the calf ID or tag, and the number or reason.")}><input type="file" accept=".csv,.txt,text/csv" aria-label={t("Choose a file")} disabled={busy} onChange={(e) => { const f = e.target.files?.[0]; if (f) void read(f); }} /></Field>;
  return (
    <div className="stack">
      {preview.duplicate_file && <p className="muted">{t("This file was brought in before. Applying it again changes nothing that's already filed.")}</p>}
      <List>{preview.rows.map((r) => <Row key={r.position} icon={r.animal ? (r.status === "registered" ? "award" : "warn") : "help"} tone={r.animal ? (r.status === "registered" ? "good" : "warn") : "neutral"}
        title={r.animal?.label ?? (r.tag ?? t("Row {{n}}", { n: r.position }))} meta={r.animal ? [r.status === "registered" ? r.number : t(r.status === "rejected" ? "Rejected" : "On hold"), r.reason].filter(Boolean).join(" · ") : r.ambiguous ? t("More than one calf matches") : t("Not in this batch")} />)}</List>
      <Field label={t("Results came back on")}><TextInput type="date" value={on} onChange={(e) => setOn(e.target.value)} /></Field>
      <div className="form-actions"><Button variant="primary" size="lg" busy={busy} disabled={!matched.length} onClick={() => void apply()}>{plural(matched.length, "File {{count}} result", "File {{count}} results")}</Button><Button variant="ghost" onClick={() => setPreview(null)}>{t("Cancel")}</Button></div>
    </div>
  );
}

function ItemSheet({ row, jobId, onClose, onDone }: { row: RegistrationRow | null; jobId: string; onClose: () => void; onDone: () => void }) {
  const [et, setEt] = useState(row?.et ?? false);
  const [cert, setCert] = useState(row?.et_certificate ?? "");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  if (!row) return null;
  const patch = async (body: Record<string, unknown>) => {
    setBusy(true);
    try { await api.patch(`/m/genetics/registration/${jobId}/items/${row.item_id}`, body); await invalidateAll(); onDone(); onClose(); }
    catch (err) { toast(err instanceof ApiError ? err.message : t("Something went wrong on our side. Try again."), { tone: "bad" }); } finally { setBusy(false); }
  };
  return (
    <Sheet open onClose={onClose} title={row.animal.label}>
      <div className="stack">
        <Toggle checked={et} onChange={setEt} label={t("ET calf")} hint={t("The dam on record is the donor. The recipient raised it.")} />
        {et && <Field label={t("ET certificate number")} optional><TextInput value={cert} onChange={(e) => setCert(e.target.value)} className="mono" /></Field>}
        <div className="form-actions"><Button variant="primary" busy={busy} onClick={() => void patch({ et, et_certificate: cert || null })}>{t("Save")}</Button></div>
        {row.item_status === "skipped"
          ? <Button busy={busy} onClick={() => void patch({ excluded: false })}>{t("Put back in this batch")}</Button>
          : <Disclosure label={t("Leave out of this batch")}>
              <Field label={t("Why")}><TextInput value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t("Going to the sale barn")} /></Field>
              <Button variant="danger" busy={busy} disabled={!reason.trim()} onClick={() => void patch({ excluded: true, reason })}>{t("Leave out")}</Button>
            </Disclosure>}
      </div>
    </Sheet>
  );
}

export function RegistrationJob({ job, refresh }: { job: JobDetail; items: JobItemDTO[]; refresh(): void }) {
  useLanguage();
  const can = useCan();
  const go = useNavigate();
  const today = useToday();
  const q = useApi<RegistrationBatchDTO>(`/m/genetics/registration/${job.id}`);
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState(false);
  const [item, setItem] = useState<RegistrationRow | null>(null);
  const [on, setOn] = useState(today);
  const [reference, setReference] = useState("");
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState<"all" | "attention">("all");
  const reload = () => { void q.refetch(); refresh(); };
  const dto = q.data;
  const shown = useMemo(() => (dto?.rows ?? []).filter((r) => filter === "all" || (r.item_status !== "skipped" && (!r.ready && !r.exported || r.outcome?.status === "rejected" || r.outcome?.status === "pending"))), [dto, filter]);
  if (q.isLoading) return <Loading />;
  if (!dto) return <ErrorNote error={q.error} retry={() => q.refetch()} />;
  const stage = dto.job.stage;
  const last = dto.files.find((f) => f.kind === "registration_export");
  const toExport = dto.rows.filter((r) => r.item_status !== "skipped" && r.ready && (!r.outcome || r.outcome.status === "rejected") && r.outcome?.status !== "registered");
  const waitingSend = dto.rows.filter((r) => r.item_status !== "skipped" && r.exported && (!r.outcome || r.outcome.status === "rejected"));
  const awaiting = dto.rows.filter((r) => r.item_status !== "skipped" && r.outcome && (r.outcome.status === "submitted" || r.outcome.status === "pending"));
  const exportFile = async () => {
    setBusy(true);
    try {
      const r = await api.post<{ file_id: string; name: string; rows: number; left_out: number }>(`/m/genetics/registration/${job.id}/export`, {});
      await invalidateAll();
      toast(plural(r.rows, "File made with {{count}} calf", "File made with {{count}} calves"), { tone: "good" });
      reload();
    } catch (err) { toast(err instanceof ApiError ? err.message : t("Something went wrong on our side. Try again."), { tone: "bad" }); } finally { setBusy(false); }
  };
  const markSent = async () => {
    setBusy(true);
    try {
      const r = await api.post<{ submitted: number }>(`/m/genetics/registration/${job.id}/submitted`, { on, reference: reference || undefined });
      await invalidateAll();
      toast(plural(r.submitted, "{{count}} calf marked sent", "{{count}} calves marked sent"), { tone: "good" });
      setSending(false);
      reload();
    } catch (err) { toast(err instanceof ApiError ? err.message : t("Something went wrong on our side. Try again."), { tone: "bad" }); } finally { setBusy(false); }
  };
  const finalStage = stage === "rejected" ? "rejected" : stage === "partial" ? "partial" : "accepted";
  return (
    <div className="stack">
      <StageSteps stages={["prepared", "exported", "submitted", finalStage]} current={stage} status={dto.job.status} />
      <div className="genetics-row-meta">
        <span>{String(dto.job.data.association ?? "")}</span>
        {dto.job.data.membership ? <span>{t("Member {{number}}", { number: String(dto.job.data.membership) })}</span> : null}
        {dto.job.data.reference ? <span>{String(dto.job.data.reference)}</span> : null}
        {dto.job.data.require_dna ? <span>{t("DNA parent check required")}</span> : null}
      </div>
      <div className="row-actions">
        <Chip tone="neutral">{t("{{ready}} of {{total}} ready", { ready: dto.counts.ready, total: dto.counts.total })}</Chip>
        {dto.counts.registered > 0 && <Chip tone="good" icon="award">{plural(dto.counts.registered, "{{count}} registered", "{{count}} registered")}</Chip>}
        {dto.counts.rejected > 0 && <Chip tone="warn" icon="warn">{plural(dto.counts.rejected, "{{count}} rejected", "{{count}} rejected")}</Chip>}
        {dto.counts.submitted + dto.counts.pending > 0 && <Chip tone="info" icon="clock">{plural(dto.counts.submitted + dto.counts.pending, "{{count}} waiting", "{{count}} waiting")}</Chip>}
      </div>
      {can("record") && (
        <Card>
          <div className="stack" style={{ gap: 10 }}>
            {waitingSend.length > 0 && last ? (
              <>
                <p><strong>{t("Next: send the file to the association, then mark it sent.")}</strong> {t("Making the file doesn't register anything.")}</p>
                <div className="row-actions">
                  <Button variant="primary" size="lg" icon="send" onClick={() => setSending(true)}>{plural(waitingSend.length, "Mark {{count}} calf sent", "Mark {{count}} calves sent")}</Button>
                  <a className="btn btn-secondary btn-md" href={fileUrl(last.id)} download>{t("Download the file")}</a>
                  {toExport.length > waitingSend.length && <Button busy={busy} onClick={() => void exportFile()}>{t("Make a new file")}</Button>}
                </div>
              </>
            ) : awaiting.length > 0 ? (
              <>
                <p><strong>{t("Waiting on the association.")}</strong> {t("When numbers come back, enter them or bring in their file.")}</p>
                <div className="row-actions"><Button variant="primary" size="lg" icon="award" onClick={() => setResults(true)}>{t("Enter results")}</Button></div>
                <Disclosure label={t("Bring in the association's file")}><ReturnedFile jobId={job.id} onDone={reload} /></Disclosure>
              </>
            ) : toExport.length > 0 ? (
              <>
                <p><strong>{plural(toExport.length, "{{count}} calf is ready for the file.", "{{count}} calves are ready for the file.")}</strong> {t("Calves that aren't ready stay out until they are.")}</p>
                <div className="row-actions"><Button variant="primary" size="lg" icon="download" busy={busy} onClick={() => void exportFile()}>{t("Make the file")}</Button></div>
              </>
            ) : dto.counts.total > 0 && dto.counts.registered === dto.counts.total ? (
              <p><Icon name="done" /> {t("Every calf in this batch is registered.")}</p>
            ) : (
              <p className="muted">{t("No calves are ready yet. Fix what each calf is missing below.")}</p>
            )}
          </div>
        </Card>
      )}
      <div className="filter-chips">
        <button type="button" className={cx("choice", filter === "all" && "choice-on")} onClick={() => setFilter("all")}>{plural(dto.rows.length, "All {{count}}", "All {{count}}")}</button>
        <button type="button" className={cx("choice", filter === "attention" && "choice-on")} onClick={() => setFilter("attention")}>{t("Needs a look")}</button>
      </div>
      <ul className="genetics-rows">
        {shown.map((r) => (
          <li key={r.item_id} className={cx("genetics-row", r.item_status === "skipped" && "is-skipped")}>
            <div className="genetics-row-head">
              <span className="genetics-row-title"><Link to={`/cattle/${r.animal.id}`} className="mono">{r.animal.label}</Link><OutcomeChip row={r} />{r.et && <Chip tone="neutral">ET</Chip>}</span>
              {can("record") && <Button size="sm" variant="ghost" icon="more" onClick={() => setItem(r)} aria-label={t("More for {{calf}}", { calf: r.animal.label })} />}
            </div>
            <span className="genetics-row-meta">
              <span>{r.animal.sex === "female" ? t("Heifer") : t("Bull")}{r.animal.birth_date ? ` · ${formatDate(r.animal.birth_date)}` : ""}</span>
              <span>{t("Sire")}: {r.sire ? `${r.sire.label}${r.sire.registration ? ` (${r.sire.registration})` : ""}` : t("Unknown")}</span>
              <span>{t("Dam")}: {r.dam ? `${r.dam.label}${r.dam.registration ? ` (${r.dam.registration})` : ""}` : t("Unknown")}</span>
            </span>
            {r.outcome?.status === "rejected" && <p className="field-err">{r.outcome.reason ?? t("No reason given")}{r.task_id ? ` · ${t("a task is open on the calf")}` : ""}</p>}
            {r.outcome?.status === "pending" && r.outcome.reason && <p className="muted">{r.outcome.reason}</p>}
            {r.item_status !== "skipped" && r.outcome?.status !== "registered" && <CheckChips checks={r.checks} onFix={(c) => fixCheck(c, r, go, () => setItem(r), String(dto.job.data.association ?? "") || undefined)} />}
          </li>
        ))}
      </ul>
      {dto.files.length > 0 && (
        <Section title={t("Files")}>
          <List>{dto.files.map((f) => <li key={f.id} className="row"><a href={fileUrl(f.id)} download className="row-inner"><span className="row-icon tone-genetics"><Icon name="file" size={18} /></span><span className="row-main"><span className="row-title">{f.name}</span><span className="row-meta">{[f.kind === "registration_export" ? t("Sent out") : t("Came back"), formatDate(f.created_at), plural(f.rows, "{{count}} row", "{{count}} rows")].join(" · ")}</span></span><Icon name="download" size={18} /></a></li>)}</List>
        </Section>
      )}
      <Sheet open={sending} onClose={() => setSending(false)} title={t("Mark sent")}>
        <form onSubmit={(e) => { e.preventDefault(); void markSent(); }}>
          <Field label={t("Sent on")}><TextInput type="date" value={on} onChange={(e) => setOn(e.target.value)} /></Field>
          <Field label={t("Their job or invoice number")} optional><TextInput value={reference} onChange={(e) => setReference(e.target.value)} /></Field>
          <div className="form-actions"><Button variant="primary" size="lg" type="submit" busy={busy}>{t("Mark sent")}</Button></div>
        </form>
      </Sheet>
      <ResultsSheet open={results} onClose={() => setResults(false)} rows={dto.rows} jobId={job.id} onDone={reload} />
      {item && <ItemSheet key={item.item_id} row={item} jobId={job.id} onClose={() => setItem(null)} onDone={reload} />}
    </div>
  );
}
